/**
 * Página pública de seguridad y privacidad (SDD §7.2, §13): qué guardamos y
 * qué no, dónde, subencargados, cómo borrar y cómo avisar que grabas. Pensada
 * para que un área de TI o de personas pueda aprobar eco.
 */
import { Link } from 'react-router'
import { Brand } from '../components/ui'
import { useApp } from '../components/AppContext'
import { useDocumentTitle } from '../lib/hooks'

const SUBENCARGADOS: [string, string, string][] = [
  ['Supabase', 'Base de datos y autenticación', 'Brasil (São Paulo)'],
  ['Cloudflare', 'Almacenamiento temporal del audio y red', 'Global'],
  ['Fly.io', 'Servidores de la aplicación', 'Brasil (São Paulo)'],
  ['AssemblyAI o Deepgram', 'Transcripción y separación de hablantes', 'Estados Unidos'],
  ['Anthropic', 'Redacción de las notas (Claude)', 'Estados Unidos'],
  ['Resend', 'Correos transaccionales', 'Estados Unidos'],
  ['Paddle', 'Cobro y facturación', 'Reino Unido'],
  ['Sentry', 'Errores técnicos, sin contenido de reuniones', 'Estados Unidos'],
  ['PostHog', 'Uso del producto, sin contenido de reuniones', 'Unión Europea']
]

export function Security() {
  useDocumentTitle('Seguridad y privacidad')
  const { session } = useApp()
  return (
    <div className="public">
      <div className="public-head">
        <Brand to={session ? '/' : '/login'} />
        <Link to={session ? '/' : '/login'} className="btn sm">
          {session ? 'Ir a eco' : 'Entrar'}
        </Link>
      </div>
      <div className="page prose">
        <h1>Seguridad y privacidad</h1>
        <p className="muted">
          eco graba tus reuniones sin meter un bot en la llamada, las transcribe y escribe notas.
          Esta página explica, sin letra chica, qué hacemos con esa información.
        </p>

        <h2>Lo más importante</h2>
        <ul>
          <li>
            <strong>No guardamos tu audio.</strong> Se sube cifrado, se transcribe y se borra de
            nuestros servidores apenas termina el procesamiento. En cada reunión ves el recibo:
            «Audio eliminado el…». Si quieres conservarlo, puedes activar en la extensión una copia
            en tu computador (viene apagada).
          </li>
          <li>
            <strong>Guardamos el texto:</strong> la transcripción, las notas y los nombres que
            pongas a los hablantes, para que puedas buscarlos y compartirlos.
          </li>
          <li>
            <strong>Nadie en eco lee tus reuniones.</strong> El soporte técnico ve solo metadatos
            (duración, estado, errores), nunca títulos ni contenido.
          </li>
          <li>
            <strong>No vendemos tus datos ni tenemos modelos propios.</strong> Anthropic, que
            redacta las notas, no entrena con lo que le enviamos. El servicio de transcripción puede
            usar el audio para mejorar su propio modelo, según su política; el detalle está en la
            política de privacidad.
          </li>
          <li>
            <strong>Tus datos están aislados.</strong> Cada organización queda separada a nivel de
            base de datos (seguridad por filas), no solo en el código.
          </li>
        </ul>

        <h2>Qué pasa con cada cosa</h2>
        <table className="simple">
          <thead>
            <tr>
              <th>Dato</th>
              <th>Dónde</th>
              <th>Cuánto tiempo</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Audio de la reunión</td>
              <td>Almacenamiento temporal cifrado</td>
              <td>
                Hasta terminar la transcripción (máximo 2 días si algo falla; 7 días si esperas
                horas del plan)
              </td>
            </tr>
            <tr>
              <td>Transcripción y notas</td>
              <td>Base de datos en Brasil</td>
              <td>
                Mientras tengas la cuenta (365 días por defecto; lo cambias en Ajustes → Privacidad)
              </td>
            </tr>
            <tr>
              <td>Grabaciones que borras</td>
              <td>Papelera</td>
              <td>7 días y luego se eliminan; los respaldos, 7 días más</td>
            </tr>
            <tr>
              <td>Texto en el proveedor de transcripción</td>
              <td>AssemblyAI / Deepgram</td>
              <td>Lo borramos apenas lo recibimos</td>
            </tr>
            <tr>
              <td>Copia local del audio (si la activas)</td>
              <td>Tu computador</td>
              <td>Tú decides</td>
            </tr>
            <tr>
              <td>Registro de seguridad (inicios de sesión, exportaciones, accesos de soporte)</td>
              <td>Base de datos en Brasil</td>
              <td>1 año</td>
            </tr>
          </tbody>
        </table>

        <h2>Subencargados</h2>
        <p>Proveedores que tratan datos por cuenta de eco, con su país:</p>
        <table className="simple">
          <thead>
            <tr>
              <th>Proveedor</th>
              <th>Para qué</th>
              <th>Dónde</th>
            </tr>
          </thead>
          <tbody>
            {SUBENCARGADOS.map(([p, f, d]) => (
              <tr key={p}>
                <td>{p}</td>
                <td>{f}</td>
                <td>{d}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <h2>Quién es responsable de los datos</h2>
        <p>
          Tu organización es la responsable de los datos de las personas que aparecen en tus
          reuniones; eco los trata por encargo, según el anexo de tratamiento que aceptas al crear
          la cuenta. Si alguien que participó en una reunión quiere ejercer sus derechos, escríbenos
          a <a href="mailto:privacidad@holaeco.cl">privacidad@holaeco.cl</a> y lo coordinamos con la
          organización en un plazo de 10 días.
        </p>

        <h2>Avisa que grabas</h2>
        <p>
          Como eco no entra a la llamada, los demás no ven un bot. Por eso te pedimos confirmar,
          cada vez, que avisaste. Una forma simple: «Voy a grabar esta reunión para tomar notas,
          ¿les parece bien?». En Chile, grabar una conversación privada sin el consentimiento de los
          participantes puede ser delito (artículo 161-A del Código Penal).
        </p>

        <h2>Cómo borrar todo</h2>
        <ul>
          <li>Una reunión: botón «Eliminar» en su detalle.</li>
          <li>
            Tu cuenta completa: Ajustes → Eliminar cuenta. Se cancela la suscripción y se borra
            todo.
          </li>
          <li>Tus datos antes de irte: Ajustes → Exportar mis datos.</li>
          <li>
            Automático: en Ajustes → Privacidad eliges cuánto se guardan las reuniones (desde 30
            días) y puedes apagar de una vez todos los links compartidos.
          </li>
        </ul>

        <h2>Controles técnicos</h2>
        <ul>
          <li>Todo viaja cifrado (TLS) y se guarda cifrado en reposo.</li>
          <li>Inicio de sesión con Google o enlace de un solo uso; sin contraseñas que filtrar.</li>
          <li>
            Los links compartidos se guardan solo como huella (hash), vencen y se pueden desactivar.
          </li>
          <li>La extensión pide solo los permisos que usa y no carga código remoto.</li>
          <li>Registro de auditoría de accesos y cambios sensibles.</li>
          <li>
            Las integraciones (Zapier, Make, n8n) reciben cada envío firmado y solo a direcciones
            públicas https: eco no puede usarse para llegar a redes internas.
          </li>
          <li>
            Las respuestas de la IA citan la transcripción y cada cita se comprueba antes de
            mostrarla; las preguntas no se guardan.
          </li>
        </ul>
        <p className="faint" style={{ marginTop: 30, fontSize: 12 }}>
          ¿Tu área de TI necesita más detalle? Escríbenos a{' '}
          <a href="mailto:seguridad@holaeco.cl">seguridad@holaeco.cl</a> y te enviamos la ficha
          técnica.
        </p>
      </div>
    </div>
  )
}
